import * as React from 'react';
import { List, Button, Box, Popover, Popper, TextField, Fade, Typography } from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import { CreateNewFolder, CloudUpload, NavigateNext, FolderOpenOutlined } from '@mui/icons-material';
import { MainPadding } from './../MainPadding';
import backgroundImage from './../grid.png';
import { FileManagerListItem } from './FileManagerListItem';
import TransformImageDialog from './TransformImageDialog';
import { notify } from '../../services/ui-dialog';
import axios from 'axios';

export function isImagePath(path: string) {
  return /[.](png|jpg|jpeg|svg|gif)$/.test(path);
}

export interface FileManagerProps {
  getItems: () => Promise<Array<{ path: string, size: any }>>;
  uploadSingleFile: (e: any, file: string, name: string, folder: string, imagifySourceUrl: string) => Promise<any>;
  deleteItem: (e: any, name: string) => Promise<void>;
  createFolder: (e: any, folder: string) => Promise<void>;
  onPick: ((filePath: string) => void) | null;
  getStaticUrl: (path: string) => string;
  renamePath: (from: string, to: string) => Promise<void>;
  transformImage: (data: any) => Promise<void>;
}

interface FileManagerState {
  newFolder: string;
  items: Array<{ path: string, size: any }>;
  uploading: boolean;
  targetFolder: string;
  newFolderOpen: boolean;
  copyAnchor: any;
  copyValue: string;
  previewAnchorEl: any;
  previewPath: string;
  duplicateTo: string,
  duplicateFrom: string,
  duplicateOpen: boolean,
  renameOpen: boolean;
  renameFrom: string;
  renameTo: string;
  popupAnchorEl: any;
  transformOpen: boolean;
  transformPath: string;
}

const HiddenFileInput = styled('input')({
  width: '1px',
  height: '1px',
  visibility: 'hidden',
  position: 'absolute',
});

const NewFolderForm = styled('form')({
  minWidth: '260px',
  padding: '8px 4px',
});

const BreadcumbWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 2,
  background: alpha(theme.palette.primary.main, 0.04),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.10)}`,
  borderRadius: 10,
  padding: '4px 6px',
  marginTop: 16,
  marginBottom: 12,
  fontSize: 13,
}));

const BreadcumbButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  minWidth: 0,
  padding: '4px 10px',
  fontSize: 13,
  fontWeight: 500,
  color: theme.palette.primary.main,
  letterSpacing: '-0.003em',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
}));

const ActionRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  flexWrap: 'wrap',
});

const ListWrapper = styled('div')(({ theme }) => ({
  border: '1px solid rgba(11, 18, 48, 0.08)',
  borderRadius: 12,
  overflow: 'hidden',
  background: '#ffffff',
  '& > .MuiList-root': {
    padding: 0,
  },
  '& .MuiListItemButton-root': {
    margin: 0,
    borderRadius: 0,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingTop: 10,
    paddingBottom: 10,
    borderBottom: '1px solid rgba(11, 18, 48, 0.05)',
  },
  '& .MuiListItemButton-root:last-of-type': {
    borderBottom: 'none',
  },
  '& .MuiListItemButton-root .MuiListItemText-primary': {
    fontWeight: 500,
    fontSize: 14,
    color: '#0b1230',
  },
}));

const EmptyState = styled('div')({
  padding: '40px 24px',
  textAlign: 'center',
  color: 'rgba(11, 18, 48, 0.5)',
  fontSize: 13,
});

class FileManager extends React.Component<FileManagerProps, FileManagerState>{

  state = {
    newFolder: '',
    items: [] as Array<{ path: string, size: any }>,
    uploading: false,
    targetFolder: '/',
    newFolderOpen: false,
    copyAnchor: null,
    copyValue: '',
    previewAnchorEl: null,
    previewPath: '',
    popupAnchorEl: null,
    renameTo: '',
    renameFrom: '',
    renameOpen: false,
    duplicateTo: '',
    duplicateFrom: '',
    duplicateOpen: false,
    transformOpen: false,
    transformPath: '',
  }

  private fileInputRef: any;
  private uploadFormRef: any;
  private buttonNewFolderRef: any;

  componentDidMount = async () => {
    this.loadItems();
  }

  loadItems = async () => {
    let items = await this.props.getItems();
    items = items.sort((a, b) => {
      const aMod = a.path.endsWith('/') ? ('____' + a.path) : a.path;
      const bMod = b.path.endsWith('/') ? ('____' + b.path) : b.path;
      return aMod.localeCompare(bMod);
    });
    this.setState({ items, uploading: false });
  }

  handleUploadFileChange = async (e: any) => {
    e.persist()
    this.setState({ uploading: true });
    try {
      await this.uploadStaticFile(e, this.state.targetFolder);
      this.uploadFormRef.reset();
      await this.loadItems();
    }
    catch (e) {
      //report fail
      this.setState({ uploading: false });
    }
  }

  handleNewFolderClick = (e: any) => {
    this.setState({ newFolderOpen: true });
  }

  handleFileInputRef = (ref: any) => {
    this.fileInputRef = ref;
  }

  handleButtonUploadClick = (e: any) => {
    this.fileInputRef.click();
  }

  handleButtonNewFolderRef = (ref: any) => {
    this.buttonNewFolderRef = ref;
  }

  handleNewFolderSubmit = async (e: any) => {
    e.preventDefault();
    await this.props.createFolder(e, this.state.targetFolder + this.state.newFolder);
    await this.loadItems();
    this.setState({ newFolder: '', newFolderOpen: false });
  }

  handleNewFolderChange = (e: any) => {
    this.setState({ newFolder: e.target.value });
  }

  handleNewFolderClose = (e: any) => {
    this.setState({ newFolder: '', newFolderOpen: false });
  }

  getHandleItemClick = (value: string) => (e: any) => {
    if (value.endsWith('/')) {
      //folder
      this.setState({ targetFolder: value });
    }
    else if (this.props.onPick != null) {
      this.props.onPick(value);
    }
    else {
      window.open(this.props.getStaticUrl(value), '_blank');
    }
  }

  handleItemDelete = async (e: any) => {
    await this.props.deleteItem(e, e.currentTarget.dataset.value);
    this.loadItems();
  }

  handleItemCopyClick = async (e: any) => {
    e.stopPropagation();
    const path = e.currentTarget.dataset.value;
    try {
      await navigator.clipboard.writeText(path);
      notify({ message: `Copied: ${path}`, severity: 'success' });
    }
    catch {
      // Fallback for browsers/contexts where the Clipboard API is unavailable
      // (e.g. http origin without permission): drop into the manual select-copy popover.
      this.setState({ copyAnchor: e.currentTarget, copyValue: path });
    }
  }

  handleCopyAreaClose = (e: any) => {
    this.setState({ copyValue: '' });
  }

  handleUploadFormRef = (ref: any) => {
    this.uploadFormRef = ref;
  }


  handleBackToRootClick = (e: any) => {
    this.setState({ targetFolder: '/' })
  }

  private uploadStaticFile: (e: any, folder: string) => Promise<any> = async (e: any, folder) => {
    const name = e.target.name;
    const promises: Array<Promise<any>> = []
    for (let index = 0; index < e.target.files.length; index++) {
      const file = e.target.files[index];

      var bodyFormData = new FormData();
      bodyFormData.append('image', file);

      try {
        if(e.target.files[0] && e.target.files[0]['type'].split('/')[0] !== 'image'){
          throw "Unsupported file extension";
        }
  
        const response = await axios.post("https://app.imagify.io/api/upload/", bodyFormData, 
            { headers: {"Authorization": `token ${import.meta.env.VITE_IMAGIFY_TOKEN}`, "Content-Type": "multipart/form-data"} });
        
        const promise = this.props.uploadSingleFile(e, file, name, folder, response.data.image);
        promises.push(promise);
      } catch (error:any) {
        const promise = this.props.uploadSingleFile(e, file, name, folder, 'optimized-already');
        promises.push(promise);
      }
    }

    return Promise.all(promises);
  }

  renderBreadcumb(path: string) {
    const breadcumb: React.ReactNode[] = [
      <BreadcumbButton key="root" size="small" onClick={this.handleBackToRootClick} startIcon={<FolderOpenOutlined sx={{ fontSize: 16 }} />}>
        Root
      </BreadcumbButton>,
    ];
    let currentFragment = '/';
    path.replace(/(\/$|^\/)/m, '').split('/').forEach((fragment: string, i: number) => {
      if (fragment) {
        breadcumb.push(
          <Box key={`next-${i}`} component="span" sx={{ display: 'inline-flex', alignItems: 'center', color: 'rgba(11, 18, 48, 0.4)' }} aria-hidden>
            <NavigateNext sx={{ fontSize: 16 }} />
          </Box>
        );
        currentFragment += (fragment + '/');
        let targetFolder = currentFragment;

        breadcumb.push(<BreadcumbButton
          key={`path-${i}`}
          onClick={() => this.setState({ targetFolder })}
          size="small">{fragment}</BreadcumbButton>
        );
      }
    })
    return breadcumb;
  }

  handleItemActiveChange = (active: boolean, previewAnchorEl: HTMLElement | null, previewPath: string) => {
    if (active) {
      this.setState({ previewAnchorEl, previewPath });
    }
    else {
      this.setState({ previewAnchorEl: null });
    }
  }

  closePreview = (e: any) => {
    this.setState({ previewAnchorEl: null });
  }

  handleRenameDialogClose = (e: any) => {
    this.setState({ renameOpen: false });
  }

  handleRenameClick = (e: any) => {
    this.setState({
      renameFrom: e.currentTarget.dataset.value,
      renameTo: e.currentTarget.dataset.value,
      popupAnchorEl: e.currentTarget,
      renameOpen: true
    });
  }

  handleRenameChange = (e: any) => {
    this.setState({
      renameTo: e.target.value,
    });
  }

  handleRenameSubmitClick = async (e: any) => {
    if (e) { e.preventDefault(); }
    try {
      await this.props.renamePath(this.state.renameFrom, this.state.renameTo);
      this.loadItems();
    }
    catch (e) {
      throw e;
    }
    finally {
      this.setState({ renameOpen: false });
    }
  }

  handleResizeClick = async (e: any) => {
    this.setState({ transformOpen: true, transformPath: e.currentTarget.dataset.value });
  }

  handleTransformClose = (e: any) => {
    this.setState({ transformOpen: false });
  }

  handleTransformExited = (e: any) => {
    this.setState({ transformPath: '' });
  }

  handleTransform = async (data: any) => {
    try {
      await this.props.transformImage(data);
      setTimeout(this.loadItems, 100);//give some time to let the image file settle down
      this.setState({ transformOpen: false });
    }
    catch (e) { throw e; }
  }

  //duplicate methods

  handleDuplicateClick = (e: any) => {
    this.setState({
      renameFrom: e.currentTarget.dataset.value,
      renameTo: e.currentTarget.dataset.value,
      popupAnchorEl: e.currentTarget,
      renameOpen: true
    });
  }

  handleDuplicateChange = (e: any) => {
    this.setState({
      renameTo: e.target.value,
    });
  }

  handleDuplicateSubmitClick = async (e: any) => {
    if (e) { e.preventDefault(); }
    try {
      //await this.props.duplicatePath(this.state.renameFrom, this.state.renameTo);
      this.loadItems();
    }
    catch (e) {
      throw e;
    }
    finally {
      this.setState({ duplicateOpen: false });
    }
  }

  handleDuplicateDialogClose = (e: any) => {
    this.setState({ duplicateOpen: false });
  }

  render() {
    const visibleItems = this.state.items.filter(x => x.path.startsWith(this.state.targetFolder));
    // Strip out the current-folder entry itself; FileManagerListItem also
    // returns null for it, but we count visible siblings to drive the empty state.
    const visibleSiblings = visibleItems.filter(x => x.path !== this.state.targetFolder);

    return (
      <div>
        <form ref={this.handleUploadFormRef}>
          <HiddenFileInput ref={this.handleFileInputRef} multiple={true} type="file" onChange={this.handleUploadFileChange} />
          <ActionRow>
            <Button
              disabled={this.state.uploading || this.props.onPick != null}
              onClick={this.handleButtonUploadClick}
              variant="contained"
              color="primary"
              startIcon={<CloudUpload />}
            >
              {this.state.uploading ? 'Uploading…' : 'Upload File'}
            </Button>
            <Button
              disabled={this.state.uploading || this.props.onPick != null}
              ref={this.handleButtonNewFolderRef}
              onClick={this.handleNewFolderClick}
              variant="outlined"
              color="primary"
              startIcon={<CreateNewFolder />}
            >
              New Folder
            </Button>
          </ActionRow>
        </form>
        <BreadcumbWrapper>
          {this.renderBreadcumb(this.state.targetFolder)}
        </BreadcumbWrapper>
        <ListWrapper>
          {visibleSiblings.length === 0 ? (
            <EmptyState>This folder is empty. Upload a file or create a sub-folder to get started.</EmptyState>
          ) : (
            <List component="nav" disablePadding>
              {visibleItems.map((x: { path: string, size: any }, i: number) => {
                return (<FileManagerListItem
                  key={`file-${i}`}
                  onClick={this.getHandleItemClick(x.path)}
                  onActiveChange={this.handleItemActiveChange}
                  filePath={x.path}
                  fileSize={x.size}
                  pickOnly={this.props.onPick != null}
                  onDeleteClick={this.handleItemDelete}
                  onCopyPathClick={this.handleItemCopyClick}
                  targetFolder={this.state.targetFolder}
                  onRenameClick={this.handleRenameClick}
                  onResizeClick={this.handleResizeClick}
                />);
              })}
            </List>
          )}
        </ListWrapper>
        <Popover
          key="new-folder"
          open={this.state.newFolderOpen}
          anchorEl={this.buttonNewFolderRef}
          onClose={this.handleNewFolderClose}
        >
          <MainPadding>
            <NewFolderForm onSubmit={this.handleNewFolderSubmit}>
              <TextField value={this.state.newFolder} onChange={this.handleNewFolderChange} margin="dense" fullWidth={true} label="Folder" />
              <Button type="submit" fullWidth={true}>Create</Button>
            </NewFolderForm>
          </MainPadding>
        </Popover>
        <Popover
          key="copy-area"
          open={!!this.state.copyValue}
          anchorEl={this.state.copyAnchor}
          onClose={this.handleCopyAreaClose}
        >
          <MainPadding>
            <NewFolderForm>
              <TextField value={this.state.copyValue} onFocus={(e) => e.currentTarget.select()} autoFocus={true} margin="dense" fullWidth={true} label="Copy" />
            </NewFolderForm>
          </MainPadding>
        </Popover>
        <Popover
          key="rename"
          open={this.state.popupAnchorEl != null && this.state.renameOpen}
          anchorEl={this.state.popupAnchorEl}
          onClose={this.handleRenameDialogClose}
        >
          <MainPadding>
            <NewFolderForm onSubmit={this.handleRenameSubmitClick}>
              <TextField onChange={this.handleRenameChange} value={this.state.renameTo} onFocus={(e) => e.currentTarget.select()} autoFocus={true} margin="dense" fullWidth={true} label="Path" />
              <div>
                <Button type="submit" color="primary" variant="contained">Rename</Button>
              </div>
            </NewFolderForm>
          </MainPadding>
        </Popover>
        <Popover
          key="duplicate"
          open={this.state.popupAnchorEl != null && this.state.duplicateOpen}
          anchorEl={this.state.popupAnchorEl}
          onClose={this.handleDuplicateDialogClose}
        >
          <MainPadding>
            <NewFolderForm onSubmit={this.handleDuplicateSubmitClick}>
              <TextField onChange={this.handleDuplicateChange} value={this.state.duplicateTo} onFocus={(e) => e.currentTarget.select()} autoFocus={true} margin="dense" fullWidth={true} label="Path" />
              <div>
                <Button type="submit" color="primary" variant="contained">Duplicate</Button>
              </div>
            </NewFolderForm>
          </MainPadding>
        </Popover>
        <TransformImageDialog
          open={this.state.transformOpen}
          path={this.state.transformPath}
          onClose={this.handleTransformClose}
          slotProps={{ transition: { onExited: this.handleTransformExited } }}
          transform={this.handleTransform}
        />
        {/*
          Hover preview is a lightweight Popper, not a Popover. Popover extends
          Modal and brings a backdrop + focus trap that intercepts clicks on
          the row's action icons (edit/copy/resize/delete) which sit underneath.
          Popper is just CSS positioning — no overlay, no click capture.
        */}
        <Popper
          key={this.state.previewPath}
          open={this.state.previewAnchorEl != null && isImagePath(this.state.previewPath)}
          anchorEl={this.state.previewAnchorEl}
          placement="left-start"
          modifiers={[{ name: 'offset', options: { offset: [0, 12] } }]}
          sx={{ zIndex: 'tooltip', pointerEvents: 'none' }}
          transition
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={150}>
              <div style={{
                background: `white url(${backgroundImage})`,
                borderRadius: 8,
                overflow: 'hidden',
                boxShadow: '0 12px 32px -8px rgba(2, 10, 48, 0.45), 0 2px 6px rgba(2, 10, 48, 0.2)',
              }}>
                <div style={{
                  width: '160px',
                  height: '160px',
                  background: `url(${this.props.getStaticUrl(this.state.previewPath)}) no-repeat center center`,
                  backgroundSize: "contain"
                }} />
              </div>
            </Fade>
          )}
        </Popper>
      </div>
    );
  }
}

export default FileManager;